/**
 * BottomSheetMyBooks.jsx
 * Мобильная шторка «Мои книги» — список сохранённых книг с возможностью
 * подгрузить любую и продолжить редактирование.
 */

import { useEffect, useState, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";
import Chip from "@mui/joy/Chip";
import Checkbox from "@mui/joy/Checkbox";
import Input from "@mui/joy/Input";
import IconButton from "@mui/joy/IconButton";

import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AspectRatioRoundedIcon from "@mui/icons-material/AspectRatioRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import BookActionsInfoModal from "../models/BookActionsInfoModal.jsx";

import BottomSheet from "./BottomSheet";
import { bookApi } from "../../api.js";
import { bookFormatLabel } from "../../utils/bookFormatLabel.js";
import { LayoutContext } from "../../context.jsx";
import { isAdminViewMode } from "../../adminViewMode.js";
import { MiniCoverPreview } from "../models/ModalMyBooks.jsx";



// ─── Карточка книги ──────────────────────────────────────────────────────────

function BookCard({ book, isCurrent, onSelect, onDelete, loading, isChecked, onToggleCheck, onRename, onChangeSize, onClone, cloning, showClone }) {
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(book.title || "");

  const spreadsCount = book.spreads?.length ?? 0;
  const pagesCount = spreadsCount > 2 ? (spreadsCount - 2) * 2 : 0;
  const accent = book.cover_bg_color || "#834A90";

  const date = book.updated_at
    ? new Date(book.updated_at).toLocaleDateString("ru-RU", {
        day: "numeric", month: "short", year: "numeric",
      })
    : "";

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      sx={{
        position: "relative",
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: "background.surface",
        border: "1.5px solid",
        borderColor: isCurrent
          ? "color-mix(in srgb, var(--joy-palette-primary-400) 50%, transparent)"
          : "divider",
        boxShadow: isCurrent
          ? "0 6px 20px rgba(0,0,0,0.10)"
          : "0 2px 10px rgba(0,0,0,0.05)",
        transition: "all 0.15s",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Акцентная полоса слева — цвет обложки книги */}
      <Box sx={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: 5,
        background: `linear-gradient(180deg, ${accent}, color-mix(in srgb, ${accent} 60%, #000))`,
      }} />

      {/* ── Строка 1: обложка (крупная) + название/мета ── */}
      <Box
        onClick={() => !isCurrent && onSelect(book)}
        sx={{
          display: "flex", alignItems: "center", gap: 1.75,
          pl: 2.25, pr: 1.75, pt: 1.75, pb: 1.25,
          cursor: isCurrent ? "default" : "pointer",
        }}
      >
        {/* Обложка крупнее, с подложкой цвета книги */}
        <Box sx={{
          flexShrink: 0, p: "4px", borderRadius: "12px",
          bgcolor: `color-mix(in srgb, ${accent} 14%, transparent)`,
        }}>
          <MiniCoverPreview book={book} width={84} height={108} />
        </Box>

        {/* Название + мета */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            {editing ? (
              <Input
                size="sm"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { onRename(book.id, editTitle.trim() || "Без названия"); setEditing(false); }
                  if (e.key === "Escape") { setEditTitle(book.title || ""); setEditing(false); }
                }}
                onClick={(e) => e.stopPropagation()}
                autoFocus
                sx={{ fontWeight: 700, fontSize: "14px", flex: 1, borderRadius: "8px" }}
                endDecorator={
                  <IconButton size="sm" variant="plain" color="success"
                    onClick={(e) => { e.stopPropagation(); onRename(book.id, editTitle.trim() || "Без названия"); setEditing(false); }}
                    sx={{ borderRadius: "6px" }}>
                    <CheckRoundedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                }
              />
            ) : (
              <>
                <Typography level="body-md"
                  sx={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "0 1 auto" }}>
                  {book.title || "Без названия"}
                </Typography>
                <Box
                  onClick={(e) => { e.stopPropagation(); setEditTitle(book.title || ""); setEditing(true); }}
                  sx={{
                    flexShrink: 0, width: 24, height: 24, borderRadius: "6px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "neutral.400", "&:active": { color: "primary.500" },
                  }}
                >
                  <EditRoundedIcon sx={{ fontSize: 14 }} />
                </Box>
                {isCurrent && (
                  <Chip size="sm" variant="soft" color="primary"
                    sx={{ fontSize: "9px", fontWeight: 700, height: 18, flexShrink: 0 }}>
                    Текущая
                  </Chip>
                )}
              </>
            )}
          </Stack>

          <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.25 }}>
            {bookFormatLabel(book)} · {pagesCount} {pagesCount === 1 ? "страница" : pagesCount < 5 ? "страницы" : "страниц"}
          </Typography>
          {date && (
            <Typography level="body-xs" sx={{ color: "text.tertiary", fontSize: "10px", mt: 0.25 }}>
              {date}
            </Typography>
          )}
        </Box>

        {/* Удалить — отдельная иконка в правом верхнем углу карточки (всегда видна) */}
        {!isCurrent && (
          <IconButton
            size="sm" variant="soft" color="danger"
            onClick={(e) => { e.stopPropagation(); if (window.confirm("Удалить эту книгу?")) onDelete(book.id); }}
            sx={{ flexShrink: 0, borderRadius: "10px", alignSelf: "flex-start" }}
          >
            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {/* ── Строка 2: галочка «Выбрать» + действия ── */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, px: 1.5, pb: 1.5, pt: 0.25 }}>
        {/* Галочка «Выбрать» с подписью */}
        <Box
          onClick={(e) => { e.stopPropagation(); onToggleCheck(book.id); }}
          sx={{
            display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0,
            px: 1, height: 36, borderRadius: "10px",
            border: "1.5px solid",
            borderColor: isChecked ? "primary.400" : "divider",
            bgcolor: isChecked ? "color-mix(in srgb, var(--joy-palette-primary-500) 8%, transparent)" : "transparent",
            cursor: "pointer",
          }}
        >
          <Checkbox
            checked={isChecked}
            onChange={(e) => { e.stopPropagation(); onToggleCheck(book.id); }}
            onClick={(e) => e.stopPropagation()}
            size="sm"
            sx={{ pointerEvents: "none" }}
          />
          <Typography level="body-xs" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
            Выбрать
          </Typography>
        </Box>

        <Button
          size="sm" variant="soft" color="neutral"
          startDecorator={<AspectRatioRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => { e.stopPropagation(); onChangeSize?.(book); }}
          sx={{ flex: 1, height: 36, borderRadius: "10px", fontSize: "12px", fontWeight: 700 }}
        >
          Размер
        </Button>
        {showClone && (
          <Button
            size="sm" variant="soft" color="success" loading={cloning}
            startDecorator={<ContentCopyRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => { e.stopPropagation(); onClone?.(book.id); }}
            sx={{ flex: 1, height: 36, borderRadius: "10px", fontSize: "12px", fontWeight: 700 }}
          >
            Копия
          </Button>
        )}
        <Button
          size="sm" variant="solid" color="primary"
          startDecorator={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => { e.stopPropagation(); onSelect(book); }}
          disabled={isCurrent}
          sx={{ flex: 1, height: 36, borderRadius: "10px", fontSize: "12px", fontWeight: 700 }}
        >
          {isCurrent ? "Открыта" : "Открыть"}
        </Button>

      </Box>
    </Box>
  );
}


// ─── Пустое состояние ────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
      <PhotoLibraryRoundedIcon sx={{ fontSize: 48, color: "neutral.300", mb: 1.5 }} />
      <Typography level="body-sm" sx={{ color: "text.tertiary", textAlign: "center" }}>
        У вас пока нет сохранённых книг.
      </Typography>
      <Typography level="body-xs" sx={{ color: "text.tertiary", textAlign: "center", mt: 0.5 }}>
        Создайте первую книгу, и она появится здесь.
      </Typography>
    </Stack>
  );
}


// ─── Главный компонент ────────────────────────────────────────────────────────

export default function BottomSheetMyBooks({ open, onClose, onSelectBook, onChangeSize, onCheckout }) {
  const { bookId, savedBookIds, setSavedBookIds, addSavedBookId, cartItems, setCartItems, selectedBookIds, setSelectedBookIds, setShowCartModal } = useContext(LayoutContext);

  const [books, setBooks]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [cloningId, setCloningId] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const handleClone = async (id) => {
    if (cloningId) return;
    setCloningId(id);
    try {
      const clone = await bookApi.cloneBook(id);
      // Добавляем клон в список сохранённых книг → ботомшит перефетчит и покажет его.
      if (clone?.id) addSavedBookId(clone.id);
    } catch (e) {
      window.alert("Не удалось клонировать книгу. Попробуйте ещё раз.");
    } finally {
      setCloningId(null);
    }
  };

  // Загружаем книги при открытии шторки
  useEffect(() => {
    if (!open || savedBookIds.length === 0) {
      setBooks([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const loadBooks = async () => {
      const results = [];
      for (const id of savedBookIds) {
        try {
          const book = await bookApi.getBook(id);
          // Заказанные книги не показываем в «Мои книги». id при этом
          // оставляем в savedBookIds — книга привязана к клиенту через заказ.
          if (book?.is_ordered && !isAdminViewMode()) continue;
          if (!cancelled) results.push(book);
        } catch (err) {
          // 404 — книги нет; 410 — медиафайлы книги очищены воркером (Правка 4).
          // В обоих случаях книга больше не редактируется — убираем из «Мои книги».
          // На прочих (сетевых/временных) ошибках книгу НЕ удаляем.
          if (err?.status === 404 || err?.status === 410) {
            console.warn(
              err?.status === 410
                ? `Книга ${id}: медиа очищены (410) — убираю из «Мои книги»`
                : `Книга ${id} не найдена (404) — убираю из сохранённых`
            );
            setSavedBookIds(prev => prev.filter(x => x !== id));
          } else {
            console.warn(`Книга ${id}: ошибка загрузки (${err?.status || "сеть"}) — оставляю в списке`);
          }
        }
      }
      if (!cancelled) {
        results.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        // Синхронизируем selectedBookIds из серверного in_cart
        const idsInCart = results.filter(b => b.in_cart).map(b => b.id);
        const idsNotInCart = results.filter(b => !b.in_cart).map(b => b.id);

        setSelectedBookIds(prev => {
          let updated = new Set(prev);
          // Добавляем те, что in_cart на сервере
          idsInCart.forEach(id => updated.add(id));
          // Убираем те, что НЕ in_cart на сервере
          idsNotInCart.forEach(id => updated.delete(id));
          return [...updated];
        });
        setBooks(results);
        setLoading(false);
      }
    };

    loadBooks();
    return () => { cancelled = true; };
  }, [open, savedBookIds]);


  const handleDelete = async (id) => {
    const cartItem = cartItems.find(item => item.bookId === id);
    if (cartItem?.cartItemId) {
      bookApi.removeFromCart(cartItem.cartItemId).catch(err =>
        console.error("Ошибка удаления из корзины:", err.message)
      );
    }

    try {
      await bookApi.deleteBook(id);
    } catch (err) {
      console.error("Ошибка удаления книги:", err.message);
    }

    setSavedBookIds(prev => prev.filter(x => x !== id));
    setBooks(prev => prev.filter(b => b.id !== id));
    setCartItems(prev => prev.filter(item => item.bookId !== id));
    setSelectedBookIds(prev => prev.filter(x => x !== id));

    if (id === bookId) {
      onSelectBook(null);
    }
  };

  const handleToggleCheck = async (id) => {
    const wasChecked = selectedBookIds.includes(id);

    if (wasChecked) {
      // ── Убираем галочку ──
      // Удаляем CartItem если есть
      const cartItem = cartItems.find(item => item.bookId === id);
      if (cartItem?.cartItemId) {
        bookApi.removeFromCart(cartItem.cartItemId).catch(err =>
          console.error("Ошибка удаления из корзины:", err.message)
        );
      }
      // Ставим in_cart = false
      bookApi.updateBook(id, { inCart: false }).catch(err =>
        console.error("Ошибка обновления in_cart:", err.message)
      );
      setCartItems(cart => cart.filter(item => item.bookId !== id));
      setSelectedBookIds(prev => prev.filter(x => x !== id));
    } else {
      // ── Ставим галочку ──
      // ТОЛЬКО обновляем in_cart, НЕ создаём CartItem!
      try {
        await bookApi.updateBook(id, { inCart: true });

        const book = books.find(b => b.id === id);
        if (book) {
          const coverPhoto = book.spreads?.[0]?.photos?.find(p => p.side === "left");
          const coverUrl = coverPhoto?.image_url ?? book.cover_template_data?.image_url ?? null;
          const alreadyInCart = cartItems.some(item => item.bookId === id);
          if (!alreadyInCart) {
            setCartItems(cart => [...cart, {
              title: book.title || "Фотокнига",
              pagesCount: (book.spreads?.length ?? 2) > 2 ? (book.spreads.length - 2) * 2 : 0,
              size: `${book.width_mm}×${book.height_mm} мм`,
              coverUrl,
              price: book.price ?? null,
              bookId: id,
              bookFormatId: book.book_format ?? null,
              widthMm: book.width_mm,
              heightMm: book.height_mm,
              cartItemId: null,  // CartItem ещё нет — создастся при validate
              bookData: book,
            }]);
          }
        }
        setSelectedBookIds(prev => [...prev, id]);
      } catch (err) {
        console.error("Ошибка обновления in_cart:", err.message);
        alert("Не удалось обновить статус книги: " + (err.message || ""));
      }
    }
  };

  const handleGoToCheckout = () => {
    onClose();
    setTimeout(() => {
      if (onCheckout) {
        onCheckout();
      } else {
        setShowCartModal(true);
      }
    }, 200);
  };

  const handleRename = async (id, newTitle) => {
    try {
      await bookApi.updateBook(id, { title: newTitle });
      setBooks(prev => prev.map(b => b.id === id ? { ...b, title: newTitle } : b));
    } catch (err) {
      console.error("Ошибка переименования:", err.message);
      alert(err.message || "Не удалось сохранить название");
    }
  };


  const handleSelect = (book) => {
    onSelectBook(book);
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Мои книги"
      footer={
        <Box sx={{ px: 2, py: 1.75, bgcolor: "background.surface", borderTop: "1px solid", borderColor: "divider" }}>
          <Stack gap={1}>
            <Button fullWidth size="sm" variant="outlined" color="primary"
              onClick={() => { onClose(); setTimeout(() => onSelectBook(null), 300); }}
              sx={{ borderRadius: "12px", fontWeight: 700 }}
            >
              + Создать новую книгу
            </Button>
            <Button
              fullWidth size="md" variant="solid" color="primary"
              startDecorator={<ShoppingCartCheckoutRoundedIcon />}
              onClick={handleGoToCheckout}
              disabled={selectedBookIds.length == 0}
              sx={{ borderRadius: "12px", fontWeight: 700 }}
            >
              Оформить заказ ({selectedBookIds.length})
            </Button>
            { selectedBookIds.length == 0 && (
              <Typography level="body-xs" sx={{ textAlign: 'center' }}>
                Выберите галочками книги, которые хотите заказать
              </Typography>
            )}
          </Stack>
        </Box>
      }
    >
      <Box sx={{ px: 2, pt: 2, pb: 3 }}>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button
            size="sm" variant="plain" color="neutral"
            startDecorator={<HelpOutlineRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setInfoOpen(true)}
            sx={{ fontSize: "12px", fontWeight: 600, borderRadius: "10px" }}
          >
            Что делают кнопки
          </Button>
        </Stack>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size="sm" />
            <Typography level="body-xs" color="neutral" mt={1.5}>
              Загружаем книги...
            </Typography>
          </Stack>
        ) : books.length === 0 ? (
          <EmptyState />
        ) : (
          <Stack gap={1.25}>
            {books.map(book => (
              <BookCard
                key={book.id}
                book={book}
                isCurrent={book.id === bookId}
                onSelect={handleSelect}
                onDelete={handleDelete}
                isChecked={selectedBookIds.includes(book.id)}
                onToggleCheck={handleToggleCheck}
                onRename={handleRename}
                onChangeSize={onChangeSize}
                onClone={handleClone}
                cloning={cloningId === book.id}
                showClone={true}
              />
            ))}
          </Stack>
        )}
      </Box>

      <BookActionsInfoModal open={infoOpen} onClose={() => setInfoOpen(false)} />
    </BottomSheet>
  );
}